import { buildApiRequestHeaders, forwardCookiesToClient, isInternalUrl, useApm, useCoreLogger, useNuxtApp, useRequestEvent, useRequestHeaders, useRequestURL, useRuntimeConfig } from '#imports'
import type { FetchOptions, FetchContext, ResponseType } from 'ofetch'
import { createFetchError, $fetch } from 'ofetch'
import { joinURL } from 'ufo'
import defu from 'defu'
import { once } from 'es-toolkit'
import type { UseApiOptions } from '../../types'
import { captureHttpError } from '../../shared/api/mutationErrorCapture'
import { redirectUnauthorized } from '../../shared/api/unauthorizedRedirect'
import { coreServerConfig } from '../../utils/coreServerConfig'

// Error helpers
function useApiErrorHandler() {
    const apm = useApm()
    const logger = useCoreLogger()

    function report(context: FetchContext) {
        const responseBody = typeof context.response?._data === 'string' ? context.response._data : JSON.stringify(context.response?._data)
        context.error = new Error(responseBody)
        const error = createFetchError(context)

        if (import.meta.server) {
            logger.withTag('useApiErrorHandler/report').error(error)
        }

        if (import.meta.client) {
            if (!apm) return
            captureHttpError({ error, response: context.response, cause: context.error }, () => apm.captureError(error))
        }
    }

    return {
        report,
    }
}

/**
 * Whether `error` is a withdrawn request — a navigation away mid-flight, or a superseded
 * search — so callers can skip reporting ordinary cancellation as an API failure. Walks
 * `cause` because ofetch and openapi-fetch wrap the DOMException. A timeout is not
 * withdrawal (`TimeoutError`) and still reports.
 */
export function isAbortError(error: unknown): boolean {
    for (let current = error; current; current = (current as { cause?: unknown }).cause) {
        if ((current as { name?: string }).name === 'AbortError') return true
    }

    return false
}

const useApiOptions = <R extends ResponseType = ResponseType>(options: UseApiOptions = {}) => {
    const { enableCookieForwarding = true, ignoreUnprocessable = false, reportErrors = true, service, ...fetchOptions } = options

    // Service config is private, so only the server can read it.
    const serviceConfig = import.meta.server && service ? coreServerConfig(useRuntimeConfig()).services?.[service] : undefined
    const event = useRequestEvent()
    const url = useRequestURL()
    const requestHeaders = useRequestHeaders()
    const apiErrorHandler = useApiErrorHandler()
    const { $i18n } = useNuxtApp()
    const getLogger = once(() => useCoreLogger().withTag('useApiOptions'))

    const { onRequest, onResponse, ...filteredFetchOptions } = fetchOptions

    let baseURL = filteredFetchOptions.baseURL

    if (serviceConfig?.server?.baseUrl) {
        baseURL = joinURL(serviceConfig.server.baseUrl, baseURL || '')
    }

    if (!baseURL) {
        baseURL = `${useRequestURL().origin}`
    }

    const preparedOptions = defu<FetchOptions<R>, FetchOptions<R>[]>(
        {
            baseURL,
            credentials: 'include',

            async onRequest(context) {
                if (import.meta.server) {
                    const url = (context.request as Request)?.url || context.request as string

                    let isOutsideInternalNetwork = false

                    if (url.startsWith('/')) {
                        if (!isInternalUrl(baseURL)) {
                            isOutsideInternalNetwork = true
                        }
                    }
                    else if (!isInternalUrl(url)) {
                        isOutsideInternalNetwork = true
                    }

                    if (isOutsideInternalNetwork) {
                        getLogger().warn({
                            message: `Request made outside of the internal network. Url is: ${url}. baseURL is: ${baseURL}`,
                            isOutsideInternalNetwork
                        })
                    }
                }

                context.options.headers = buildApiRequestHeaders({
                    event: event!,
                    requestHeaders,
                    extraHeaders: {
                        ...fetchOptions.headers || {},
                        ...Object.fromEntries(context.options.headers || {}),
                    },
                })

                if (onRequest) {
                    if (Array.isArray(onRequest)) {
                        return Promise.allSettled(onRequest.map(callback => callback(context)))
                    }
                    return onRequest(context)
                }
            },

            onRequestError(context) {
                if (isAbortError(context.error)) return

                if (reportErrors) apiErrorHandler.report(context)
            },

            async onResponse(context) {
                if (import.meta.server) {
                    if (enableCookieForwarding) {
                        forwardCookiesToClient(event!, context.response.headers)
                    }
                }

                if (onResponse) {
                    if (Array.isArray(onResponse)) {
                        return Promise.allSettled(onResponse.map(callback => callback(context)))
                    }
                    return onResponse(context)
                }
            },
            async onResponseError(context) {
                const isIgnoredUnprocessable = ignoreUnprocessable && context.response.status === 422

                if (reportErrors && !isIgnoredUnprocessable) apiErrorHandler.report(context)

                if (import.meta.client && context.response?.status === 401) {
                    const requestUrl = typeof context.request === 'string' ? context.request : context.request.url
                    await redirectUnauthorized($i18n.t('pages.lobby'), requestUrl)
                }
            },
        },
        filteredFetchOptions as FetchOptions<R>
    )

    return preparedOptions
}

export const useApi = (options: UseApiOptions = {}) => {
    const apiOptions = useApiOptions(options)

    const instance = $fetch.create(apiOptions)

    return instance
}
